import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { Stamp } from "@/components/fronk/Stamp";

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export const ChairmanMemo = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yA = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [36, -36]);
  const yB = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [-24, 24]);

  return (
    <section
      id="chairman"
      ref={ref}
      className="paper-grain relative overflow-hidden bg-[color:var(--paper-1)] py-16 sm:py-20 lg:py-28"
      data-testid="chairman-memo-section"
    >
      <motion.img
        src="/fronk/props/receipt-stack.webp"
        alt=""
        style={{ y: yB }}
        className="absolute right-[4%] top-10 z-0 hidden w-32 rotate-6 opacity-80 lg:block"
      />
      <motion.img
        src="/fronk/props/brass-desk-lamp.webp"
        alt=""
        style={{ y: yA }}
        className="absolute bottom-16 left-[3%] z-0 hidden w-24 -rotate-3 opacity-80 lg:block"
      />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp}>
          <Stamp>From the desk of the chairman</Stamp>
          <h2 className="font-heading mt-5 max-w-2xl text-3xl font-bold leading-tight text-[color:var(--ink-900)] sm:text-4xl lg:text-5xl">
            Nobody knows what FRONK did before this job.
          </h2>
          <p className="font-body mt-4 max-w-xl text-base leading-relaxed text-[color:var(--ink-800)]">
            He was appointed anyway. The staff respects him. The reasons are
            unclear and morale is excellent.
          </p>
        </motion.div>

        <div className="mt-12 grid items-start gap-10 lg:grid-cols-12">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <div className="ink-frame relative rounded-2xl bg-[color:var(--paper-0)] p-6 sm:p-8">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--hairline)] pb-4">
                <span className="font-mono-ledger text-[11px] uppercase tracking-[0.2em] text-[color:var(--ink-700)]">
                  Internal memo ref 004 A
                </span>
                <span className="font-mono-ledger text-[11px] uppercase tracking-[0.2em] text-[color:var(--stamp-600)]">
                  Subject: the tubes, again
                </span>
              </div>
              <div className="font-body mt-5 space-y-4 text-[15px] leading-relaxed text-[color:var(--ink-900)]">
                <p>To all desks.</p>
                <p>
                  I have been told the tubes are at capacity again. Good. That
                  is what the tubes are for.
                </p>
                <p>
                  Our business remains simple. Trading fees arrive. The staff
                  processes them. Envelopes now leave the mailroom with TOAD
                  inside. The staff remains Pepe. Accounting marked this as a
                  currency change and nobody received a new uniform. I sign
                  what needs signing and answer the red phone whenever I can
                  find the correct end.
                </p>
                <p>
                  Keep your desks stocked. The mail goes out when the mail goes
                  out.
                </p>
                <p className="pt-2">
                  Yours in maximum seriousness,
                  <br />
                  <span className="font-heading text-xl font-bold">FRONK</span>
                  <br />
                  <span className="font-mono-ledger text-xs uppercase tracking-[0.16em] text-[color:var(--ink-700)]">
                    Chairman, seventh floor
                  </span>
                </p>
              </div>
              <img
                src="/fronk/hero/fronk-wordmark-seal.webp"
                alt="FRONK company seal"
                className="absolute -bottom-6 -right-4 w-24 rotate-[8deg] drop-shadow-md sm:w-28"
              />
            </div>

            <motion.div
              {...fadeUp}
              className="mt-8 flex items-start gap-4 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--paper-0)] p-5 shadow-[var(--paper-lift)]"
            >
              <img
                src="/fronk/props/red-rotary-phone.webp"
                alt="The red rotary phone"
                className="w-16 shrink-0 -rotate-6"
              />
              <p className="font-body text-sm leading-relaxed text-[color:var(--ink-800)]">
                The red phone sits on his desk at all times. He holds it upside
                down. Compliance has filed three notes about it. All three
                notes were signed by FRONK without reading them.
              </p>
            </motion.div>
          </motion.div>

          <div className="relative flex flex-col items-center lg:col-span-5">
            <motion.div
              {...fadeUp}
              className="ink-frame w-40 rotate-3 rounded-xl bg-[color:var(--paper-0)] p-2 sm:w-44 lg:self-end"
            >
              <img
                src="/fronk/scenes/fronk-corporate-portrait.webp"
                alt="Official corporate portrait of chairman FRONK"
                className="rounded-lg"
                loading="lazy"
              />
              <p className="font-mono-ledger px-1 py-2 text-center text-[9px] uppercase tracking-[0.14em] text-[color:var(--ink-700)]">
                Official portrait. Fiscal year one.
              </p>
            </motion.div>
            <motion.img
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              src="/fronk/characters/fronk-chairman-phone.webp"
              alt="FRONK holding the red phone and a stack of green folders"
              className="mt-8 w-60 drop-shadow-xl sm:w-72 lg:mt-10 lg:self-start"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
